import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
} from "../../ui/dialog";
import { Drawer, DrawerContent } from "../../ui/drawer";
import { Calendar } from "../../ui/calendar";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { format } from "date-fns";
import axios from "axios";
import { POINTS_CONFIG } from "../../../api/mock/userData";
import {
  CalendarDays,
  CalendarIcon,
  Car,
  AlertCircle,
  CheckCircle,
  Info,
  ChevronRight,
  ChevronLeft,
  Clock,
  CreditCard,
  X,
  MapPin,
  Users,
} from "lucide-react";
import { Alert, AlertDescription } from "../../ui/alert";
import { useTranslation } from 'react-i18next';

const ReserveParkingModal = ({ isOpen, onClose, onReserve, userId }) => {
  const { t } = useTranslation();
  const [date, setDate] = useState({ from: null, to: null });
  const [userStats, setUserStats] = useState(null);
  const [userParkingSpot, setUserParkingSpot] = useState(null);
  const [availableCount, setAvailableCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityArrayIds, setAvailabilityArrayIds] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      
      try {
        const token = localStorage.getItem("access_token");
        const userApiInstance = axios.create({
          baseURL: process.env.REACT_APP_BACKEND_URL + "api/users",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });
        
        const parkingApiInstance = axios.create({
          baseURL: process.env.REACT_APP_BACKEND_URL + "api/parking",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });

        const [stats, userParking] = await Promise.all([
          Promise.all([
            userApiInstance.post("/me/points", { userId }),
            userApiInstance.post("/me/points/transactions", { userId }),
            userApiInstance.post("/me/recommendations", { userId }),
            userApiInstance.post("/me/active-spots", { userId }),
            userApiInstance.post("/me/total-earnings", { userId })
          ]).then(([points, transactions, recommendations, activeSpots, totalEarnings]) => ({
            points: points.data.points,
            transactions: transactions.data.transactions,
            recommendationsCount: recommendations.data.count,
            likesCount: recommendations.data.likesCount,
            activeSpots: activeSpots.data.count,
            totalEarnings: totalEarnings.data.earnings,
          })),
          parkingApiInstance.get(`/user-spot/${userId}`).then(response => response.data)
        ]);
        
        setUserStats(stats);
        setUserParkingSpot(userParking);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [userId]);

  useEffect(() => {
    const checkAvailability = async () => {
      if (!date.from || !date.to || !userId) {
        setAvailableCount(0);
        return;
      }

      setCheckingAvailability(true);
      try {
        const startDateStr =
          date.from.getFullYear() +
          "-" +
          String(date.from.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(date.from.getDate()).padStart(2, "0");

        const endDateStr =
          date.to.getFullYear() +
          "-" +
          String(date.to.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(date.to.getDate()).padStart(2, "0");

        const token = localStorage.getItem("access_token");
        const parkingApiInstance = axios.create({
          baseURL: process.env.REACT_APP_BACKEND_URL + "api/parking",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });

        const response = await parkingApiInstance.post("/check-availability", {
          startDate: startDateStr,
          endDate: endDateStr,
          excludeUserId: userId,
          excludeUserSpot: true,
        });

        const count = response.data.availableCount.length;
        setAvailabilityArrayIds(response.data.availableCount);
        setAvailableCount(count);
      } catch (error) {
        console.error("Error checking availability:", error);
        setAvailableCount(0);
      } finally {
        setCheckingAvailability(false);
      }
    };

    checkAvailability();
  }, [date.from, date.to, userId]);

  const calculateTotalCost = () => {
    if (!date.from || !date.to) return 0;
    const days = Math.ceil((date.to - date.from) / (1000 * 60 * 60 * 24)) + 1;
    return days * POINTS_CONFIG.PARKING_COST_PER_DAY;
  };

  const handleReserve = async () => {
    setLoading(true);
    const randomSpotId =
      availabilityArrayIds[
        Math.floor(Math.random() * availabilityArrayIds.length)
      ];
    try {
      await onReserve({
        spotId: randomSpotId,
        startDate: date.from,
        endDate: date.to,
        totalCost: calculateTotalCost(),
      });
    } catch (error) {
      console.error("Error making reservation:", error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const totalCost = calculateTotalCost();
  const hasEnoughPoints = userStats?.points >= totalCost;
  const canProceed = date.from && date.to && availableCount > 0;
  const canReserve = canProceed && hasEnoughPoints && !loading;

  const handleNext = () => {
    if (canProceed) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  // Reset step when modal closes/opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  // Mobile Content - Improved with better scrolling
  const MobileContent = () => (
    // make sure a scroll bare is here when the content is too long
    <div className="flex flex-col h-full max-h-full bg-gray-50 dark:bg-gray-900 overflow-y-auto">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Car className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('parking.reserve.title')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('parking.reserve.subtitle')}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              1
            </div>
            <span className={`text-sm ${currentStep >= 1 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
              {t('parking.reserve.steps.step1')}
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              2
            </div>
            <span className={`text-sm ${currentStep >= 2 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
              {t('parking.reserve.steps.step2')}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ minHeight: 0 }}>
        {/* Current Parking Alert */}
        {userParkingSpot && (
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="h-4 w-4" />
                <strong>{t('parking.reserve.currentSpot', { spotNumber: userParkingSpot.spotNumber })}</strong>
              </div>
              <span className="text-xs">{t('parking.reserve.reservingAdditional')}</span>
            </AlertDescription>
          </Alert>
        )}

        {currentStep === 1 && (
          <div className="space-y-4">
            {/* Calendar Section */}
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-medium">{t('parking.reserve.selectDates')}</h3>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <Calendar
                  mode="range"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => date < new Date()}
                  className="w-full"
                />
              </div>
            </Card>

            {/* Selected Period Summary */}
            {date.from && date.to && (
              <Card className="p-4">
                <h4 className="font-semibold mb-3 text-center text-sm text-gray-700 dark:text-gray-300">
                  {t('parking.reserve.selectedPeriod.title')}
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{t('parking.reserve.selectedPeriod.from')}</span>
                    </div>
                    <span className="font-medium text-sm">
                      {format(date.from, "MMM d, yyyy")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{t('parking.reserve.selectedPeriod.until')}</span>
                    </div>
                    <span className="font-medium text-sm">
                      {format(date.to, "MMM d, yyyy")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <span className="text-blue-700 dark:text-blue-300 text-sm font-medium">{t('parking.reserve.selectedPeriod.duration')}</span>
                    </div>
                    <span className="font-semibold text-blue-700 dark:text-blue-300 text-sm">
                      {Math.ceil((date.to - date.from) / (1000 * 60 * 60 * 24)) + 1} {t('parking.reserve.selectedPeriod.days')}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* Availability Status */}
            {date.from && date.to && (
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <h4 className="font-medium">{t('parking.reserve.availability.title')}</h4>
                </div>
                
                {checkingAvailability ? (
                  <div className="flex items-center justify-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('parking.reserve.availability.checking')}</span>
                  </div>
                ) : (
                  <div className={`p-4 rounded-lg border ${
                    availableCount > 0
                      ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800"
                      : "bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800"
                  }`}>
                    <div className="flex items-center gap-3">
                      {availableCount > 0 ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-red-600" />
                      )}
                      <div>
                        <p className={`font-medium ${
                          availableCount > 0 ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"
                        }`}>
                          {availableCount > 0
                            ? t('parking.reserve.availability.available', { count: availableCount })
                            : t('parking.reserve.availability.notAvailable')}
                        </p>
                        <p className={`text-xs mt-1 ${
                          availableCount > 0 ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"
                        }`}>
                          {availableCount > 0
                            ? t('parking.reserve.availability.availableDesc')
                            : t('parking.reserve.availability.notAvailableDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            {/* Back Button */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleBack} className="p-2">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="text-lg font-medium">{t('parking.reserve.steps.step2')}</h3>
            </div>

            {/* Cost Summary */}
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-blue-600" />
                <h4 className="font-medium">{t('parking.reserve.costBreakdown.title')}</h4>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('parking.reserve.costBreakdown.costPerDay')}</span>
                  <span className="font-medium text-sm">{POINTS_CONFIG.PARKING_COST_PER_DAY} {t('parking.reserve.points')}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('parking.reserve.costBreakdown.totalDays')}</span>
                  <span className="font-medium text-sm">
                    {Math.ceil((date.to - date.from) / (1000 * 60 * 60 * 24)) + 1}
                  </span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-lg">{t('parking.reserve.costBreakdown.totalCost')}</span>
                    <span className="font-bold text-xl text-blue-600">{totalCost} {t('parking.reserve.points')}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{t('parking.reserve.costBreakdown.yourBalance')}</span>
                    <span className={`font-medium ${
                      hasEnoughPoints ? "text-green-600" : "text-red-600"
                    }`}>
                      {userStats?.points || 0} {t('parking.reserve.points')}
                    </span>
                  </div>
                  {hasEnoughPoints && totalCost > 0 && (
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-gray-600 dark:text-gray-400">{t('parking.reserve.costBreakdown.afterPurchase')}</span>
                      <span className="font-medium text-green-600">
                        {(userStats?.points || 0) - totalCost} {t('parking.reserve.points')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {!hasEnoughPoints && totalCost > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>{t('parking.reserve.costBreakdown.insufficientPoints')}</strong>
                  <br />
                  {t('parking.reserve.costBreakdown.insufficientPointsDesc', { 
                    points: totalCost - (userStats?.points || 0) 
                  })}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg z-10">
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="flex-1"
          >
            {t('parking.reserve.buttons.cancel')}
          </Button>
          
          {currentStep === 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className="flex-1"
            >
              {t('parking.reserve.buttons.next')} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleReserve}
              disabled={!canReserve}
              className="flex-1"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  <span>{t('parking.reserve.buttons.processing')}</span>
                </div>
              ) : (
                t('parking.reserve.buttons.reserve')
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

     // Desktop Content - Modern layout with better organization
   const DesktopContent = () => (
     <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 min-h-0">
             {/* Header */}
       <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 shadow-sm z-10">
         <div className="flex items-center justify-between mb-2">
           <div className="flex items-center gap-2">
             <div className="p-1.5 bg-blue-100 dark:bg-blue-900 rounded-lg">
               <Car className="h-4 w-4 text-blue-600 dark:text-blue-400" />
             </div>
             <div>
               <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('parking.reserve.title')}</h2>
               <p className="text-xs text-gray-600 dark:text-gray-400">
                 {t('parking.reserve.subtitle')}
               </p>
             </div>
           </div>
           <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
             <X className="h-3 w-3" />
           </Button>
         </div>

                 {/* Progress Steps */}
         <div className="flex items-center justify-center gap-4">
           <div className="flex items-center gap-1.5">
             <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
               currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
             }`}>
               1
             </div>
             <span className={`text-xs font-medium ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-500'}`}>
               {t('parking.reserve.steps.step1')}
             </span>
           </div>
           <ChevronRight className="w-3 h-3 text-gray-400" />
           <div className="flex items-center gap-1.5">
             <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
               currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
             }`}>
               2
             </div>
             <span className={`text-xs font-medium ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-500'}`}>
               {t('parking.reserve.steps.step2')}
             </span>
           </div>
         </div>
      </div>

             {/* Main Content */}
       <div className="flex-1 overflow-y-auto min-h-0 flex-shrink-0">
         <div className="h-full flex min-h-0">
                      {/* Left Panel - Calendar & Current Info */}
           <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 p-3 min-h-0 flex-shrink-0">
             <div className="space-y-4">
              {/* Current Parking Info */}
              {userParkingSpot && (
                <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <AlertDescription className="text-blue-800 dark:text-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4" />
                      <strong className="text-base">
                        {userParkingSpot.location 
                          ? t('parking.reserve.currentSpotLocation', { spotNumber: userParkingSpot.spotNumber, location: userParkingSpot.location })
                          : t('parking.reserve.currentSpotFull', { spotNumber: userParkingSpot.spotNumber })
                        }
                      </strong>
                    </div>
                    <p className="text-sm">{t('parking.reserve.reservingAdditionalDesc')}</p>
                  </AlertDescription>
                </Alert>
              )}

                             {/* Calendar Section */}
               <Card className="p-3">
                 <div className="flex items-center gap-2 mb-3">
                   <CalendarIcon className="h-4 w-4 text-blue-600" />
                   <h3 className="text-base font-semibold">{t('parking.reserve.selectDates')}</h3>
                 </div>

                                 <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                  <Calendar
                    mode="range"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date()}
                    className="w-full"
                  />
                </div>
              </Card>

                             {/* Selected Period Summary */}
               {date.from && date.to && (
                 <Card className="p-3">
                   <h4 className="font-semibold mb-2 text-center text-xs text-gray-700 dark:text-gray-300">
                     {t('parking.reserve.selectedPeriod.title')}
                   </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-600 dark:text-gray-400 text-sm">{t('parking.reserve.selectedPeriod.from')}</span>
                      </div>
                      <span className="font-semibold text-sm">
                        {format(date.from, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-600 dark:text-gray-400 text-sm">{t('parking.reserve.selectedPeriod.until')}</span>
                      </div>
                      <span className="font-semibold text-sm">
                        {format(date.to, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-700 dark:text-blue-300 font-medium text-sm">{t('parking.reserve.selectedPeriod.duration')}</span>
                      </div>
                      <span className="font-bold text-blue-700 dark:text-blue-300 text-sm">
                        {Math.ceil((date.to - date.from) / (1000 * 60 * 60 * 24)) + 1} {t('parking.reserve.selectedPeriod.days')}
                      </span>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>

                                {/* Right Panel - Information & Actions */}
           <div className="w-1/2 p-3 min-h-0 flex-shrink-0">
             {date.from && date.to ? (
               <div className="space-y-4">
                                 {/* Availability Section */}
                 <Card className="p-3">
                   <div className="flex items-center gap-2 mb-2">
                     <Users className="h-4 w-4 text-blue-600" />
                     <h3 className="text-base font-semibold">{t('parking.reserve.availability.title')}</h3>
                   </div>

                  {checkingAvailability ? (
                    <div className="flex items-center justify-center gap-3 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="text-base text-gray-600 dark:text-gray-400">
                        {t('parking.reserve.availability.checking')}
                      </span>
                    </div>
                  ) : (
                    <div className={`p-4 rounded-lg border-2 ${
                      availableCount > 0
                        ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800"
                        : "bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800"
                    }`}>
                      <div className="flex items-start gap-3">
                        {availableCount > 0 ? (
                          <CheckCircle className="w-8 h-8 text-green-600 mt-1 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-8 h-8 text-red-600 mt-1 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className={`text-lg font-bold mb-1 ${
                            availableCount > 0
                              ? "text-green-800 dark:text-green-200"
                              : "text-red-800 dark:text-red-200"
                          }`}>
                            {availableCount > 0
                              ? t('parking.reserve.availability.available', { count: availableCount })
                              : t('parking.reserve.availability.notAvailable')}
                          </h4>
                          <p className={`text-sm ${
                            availableCount > 0
                              ? "text-green-700 dark:text-green-300"
                              : "text-red-700 dark:text-red-300"
                          }`}>
                            {availableCount > 0
                              ? t('parking.reserve.availability.availableDesc')
                              : t('parking.reserve.availability.notAvailableDesc')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>

                                 {/* Cost Summary */}
                 <Card className="p-3">
                   <div className="flex items-center gap-2 mb-3">
                     <CreditCard className="h-4 w-4 text-blue-600" />
                     <h3 className="text-base font-semibold">{t('parking.reserve.costBreakdown.title')}</h3>
                   </div>

                                     <div className="space-y-3">
                     <div className="grid grid-cols-1 gap-2">
                       <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                         <span className="text-gray-600 dark:text-gray-400 text-sm">
                           {t('parking.reserve.costBreakdown.costPerDay')}
                         </span>
                         <span className="font-semibold text-sm">
                           {POINTS_CONFIG.PARKING_COST_PER_DAY} {t('parking.reserve.points')}
                         </span>
                       </div>
                       <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                         <span className="text-gray-600 dark:text-gray-400 text-sm">{t('parking.reserve.costBreakdown.totalDays')}</span>
                         <span className="font-semibold text-sm">
                           {Math.ceil((date.to - date.from) / (1000 * 60 * 60 * 24)) + 1}
                         </span>
                       </div>
                     </div>

                    <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-6">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-xl font-bold">
                          {t('parking.reserve.costBreakdown.totalCost')}
                        </span>
                        <span className="text-3xl font-bold text-blue-600">
                          {totalCost} {t('parking.reserve.points')}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">
                            {t('parking.reserve.costBreakdown.yourBalance')}
                          </span>
                          <span className={`font-semibold text-lg ${
                            hasEnoughPoints ? "text-green-600" : "text-red-600"
                          }`}>
                            {userStats?.points || 0} {t('parking.reserve.points')}
                          </span>
                        </div>

                        {hasEnoughPoints && totalCost > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">
                              {t('parking.reserve.costBreakdown.afterPurchase')}
                            </span>
                            <span className="font-semibold text-green-600 text-lg">
                              {(userStats?.points || 0) - totalCost} {t('parking.reserve.points')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>

                {!hasEnoughPoints && totalCost > 0 && (
                  <Alert variant="destructive" className="border-2">
                    <AlertCircle className="h-5 w-5" />
                    <AlertDescription className="text-base">
                      <strong>{t('parking.reserve.costBreakdown.insufficientPoints')}</strong>
                      <br />
                      {t('parking.reserve.costBreakdown.insufficientPointsDesc', { 
                        points: totalCost - (userStats?.points || 0) 
                      })}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-muted-foreground">
                  <CalendarDays className="h-16 w-16 mx-auto mb-6 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">{t('parking.reserve.emptyState.title')}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{t('parking.reserve.emptyState.description')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

                    {/* Footer */}
       <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-3 shadow-lg z-10">
         <div className="flex justify-end gap-3">
           <Button 
             variant="outline" 
             onClick={onClose}
             size="default"
           >
             {t('parking.reserve.buttons.cancel')}
           </Button>
           
           <Button
             onClick={handleReserve}
             disabled={!canReserve}
             size="default"
             className="min-w-[160px]"
           >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('parking.reserve.buttons.processing')}</span>
              </div>
            ) : (
              t('parking.reserve.buttons.reserve')
            )}
          </Button>
        </div>
      </div>
    </div>
  );

     if (isMobile) {
     return (
             <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[95vh] overflow-hidden flex flex-col">
          <MobileContent />
        </DrawerContent>
      </Drawer>
     );
   }

                 return (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="w-[95vw] max-w-[1400px] h-[90vh] p-0 overflow-hidden max-h-[90vh]">
            <DesktopContent />
          </DialogContent>
        </Dialog>
      );
};

export default ReserveParkingModal;
